/** Host OS — Settings. Host profile summary + event preferences. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, Bell, Globe, CreditCard, ExternalLink, RotateCcw, BookOpen, UsersRound } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, PreviewBadge } from "@/components/host/hostUi";

const initials = (n) => (!n ? "?" : n.split(" ").map((x) => x[0]).join("").toUpperCase().slice(0, 2));

const PREFS = [
  { id: "booking", icon: Bell, title: "Email me on every new booking", desc: "Get an instant email whenever someone books one of your events.", defaultOn: true },
  { id: "cancel", icon: Bell, title: "Email me on cancellations", desc: "Know right away when someone drops out.", defaultOn: true },
  { id: "digest", icon: Bell, title: "Weekly performance digest", desc: "A Monday summary of bookings, revenue and upcoming events.", defaultOn: false },
];

const HostSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(() => Object.fromEntries(PREFS.map((p) => [p.id, p.defaultOn])));
  const [currency, setCurrency] = useState("USD");

  return (
    <HostLayout>
      <div data-testid="host-settings-page">
        <HostPageHeader
          icon={Settings}
          title="Settings"
          subtitle="Manage your host profile, notifications and event defaults."
        />

        {/* Profile summary */}
        <Card className="p-5 mb-5" data-testid="settings-profile">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 ring-1 ring-border">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary/20 text-primary-ink text-lg">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-display text-2xl leading-tight truncate">{user?.name || "Host"}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/profile")} data-testid="settings-edit-profile">
              <ExternalLink className="w-4 h-4" /> Edit profile
            </Button>
          </div>
        </Card>

        {/* Knowledge Base */}
        <Card className="p-5 mb-6 border-primary/30" data-testid="settings-kb-card">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary-ink flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl leading-tight">Knowledge Base</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your source of truth. Connect your website, files and notes so the platform and every AI tool answers accurately about your events.
              </p>
            </div>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0" onClick={() => navigate("/host/knowledge")} data-testid="settings-open-kb">
              <BookOpen className="w-4 h-4" /> Open
            </Button>
          </div>
        </Card>

        {/* Team */}
        <Card className="p-5 mb-6" data-testid="settings-team-card">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <UsersRound className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl leading-tight">Team</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Add co-hosts and staff to the platform and set what each person can access.
              </p>
            </div>
            <Button variant="outline" className="gap-1.5 shrink-0" onClick={() => navigate("/host/team")} data-testid="settings-open-team">
              <UsersRound className="w-4 h-4" /> Manage team
            </Button>
          </div>
        </Card>

        {/* Notifications */}
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-primary-ink" />
          <h2 className="font-display text-xl">Notifications</h2>
          <PreviewBadge />
        </div>
        <Card className="divide-y divide-border mb-6" data-testid="settings-notifications">
          {PREFS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} className="flex items-center gap-3 p-4" data-testid={`pref-${p.id}`}>
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.desc}</p>
                </div>
                <Switch
                  checked={prefs[p.id]}
                  onCheckedChange={(v) => { setPrefs((s) => ({ ...s, [p.id]: v })); toast.message("Preference updated (preview)"); }}
                  data-testid={`pref-toggle-${p.id}`}
                />
              </div>
            );
          })}
        </Card>

        {/* Event defaults */}
        <div className="grid lg:grid-cols-2 gap-3">
          <Card className="p-5" data-testid="settings-defaults">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-primary-ink" />
              <h3 className="font-display text-lg">Event defaults</h3>
              <PreviewBadge />
            </div>
            <label className="text-sm text-muted-foreground">Default currency for new events</label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="mt-1.5" data-testid="settings-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="GBP">GBP (£)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
              </SelectContent>
            </Select>
          </Card>

          <Card className="p-5" data-testid="settings-billing-link">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-primary-ink" />
              <h3 className="font-display text-lg">Payouts</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Manage how and when you get paid for your events.</p>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/host/billing")} data-testid="settings-open-billing">
              <CreditCard className="w-4 h-4" /> Open billing
            </Button>
          </Card>
        </div>

        {/* Player view */}
        <div className="mt-6">
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => navigate("/")} data-testid="settings-switch-player">
            <RotateCcw className="w-4 h-4" /> Switch to player view
          </Button>
        </div>
      </div>
    </HostLayout>
  );
};

export default HostSettingsPage;
